/**
 * Controlled room-type values for the Apartments collection.
 * Add new types here when inventory introduces a new category.
 */
export const roomTypeIds = [
  'classic',
  'deluxe',
  'penthouse',
  'neonGold',
  'townhouse',
  'cedar',
  'fir',
  'pine',
  'sequoia',
  'double',
  'doubleDeluxe',
  'neonSilverGold',
  'comfort',
  'cozy',
  'superior',
] as const

export type RoomTypeId = (typeof roomTypeIds)[number]

export const roomTypeSelectOptions = [
  { label: 'Classic', value: 'classic' },
  { label: 'Deluxe', value: 'deluxe' },
  { label: 'Penthouse', value: 'penthouse' },
  { label: 'Neon Gold', value: 'neonGold' },
  { label: 'Townhouse', value: 'townhouse' },
  { label: 'Cedar', value: 'cedar' },
  { label: 'Fir', value: 'fir' },
  { label: 'Pine', value: 'pine' },
  { label: 'Sequoia', value: 'sequoia' },
  { label: 'Double', value: 'double' },
  { label: 'Double Deluxe', value: 'doubleDeluxe' },
  { label: 'Neon Silver / Gold', value: 'neonSilverGold' },
  { label: 'Comfort', value: 'comfort' },
  { label: 'Cozy', value: 'cozy' },
  { label: 'Superior', value: 'superior' },
] as const

const ROOM_TYPE_LABELS = Object.fromEntries(
  roomTypeSelectOptions.map((option) => [option.value, option.label]),
) as Record<RoomTypeId, string>

export function isRoomTypeId(value: string): value is RoomTypeId {
  return (roomTypeIds as readonly string[]).includes(value)
}

export function getRoomTypeLabel(roomType: RoomTypeId): string {
  return ROOM_TYPE_LABELS[roomType] ?? roomType
}

/**
 * Extract the WordPress apartment category slug from a legacy pathname.
 * e.g. `/apartment/fir/berlin-rix-neukoelln-fir-classic/` → `fir`
 */
export function extractLegacyApartmentCategorySlug(
  pathname: string | null | undefined,
): string | null {
  if (!pathname) return null

  const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  const segments = normalized.split('/').filter(Boolean)
  const apartmentIndex = segments.findIndex((segment) => segment === 'apartment')
  if (apartmentIndex < 0) return null

  const after = segments.slice(apartmentIndex + 1)
  return after[0] ?? null
}

/**
 * Infer room type from a legacy WordPress category slug when available.
 * Returns null when the slug is unknown so callers can fall back to title inference.
 */
export function inferRoomTypeFromCategorySlug(
  categorySlug: string | null | undefined,
): RoomTypeId | null {
  if (!categorySlug) return null

  const normalized = categorySlug.trim().toLowerCase()

  if (normalized.includes('cedar')) return 'cedar'
  if (normalized.includes('pine')) return 'pine'
  if (normalized.includes('sequoia') || normalized.includes('sequioa')) return 'sequoia'
  if (
    normalized === 'fir' ||
    normalized.endsWith('-fir') ||
    normalized.includes('-fir-') ||
    normalized.includes('rix-fir')
  ) {
    return 'fir'
  }

  if (normalized.includes('double-deluxe') || normalized.includes('double_deluxe')) {
    return 'doubleDeluxe'
  }

  if (
    normalized.includes('neon-silver') ||
    normalized.includes('neon_silver') ||
    normalized.includes('silver_gold') ||
    normalized.includes('silver-gold')
  ) {
    return 'neonSilverGold'
  }

  if (
    normalized.includes('neon-gold') ||
    normalized.includes('neon_gold') ||
    normalized.includes('gold_gold') ||
    normalized.includes('gold-balcony')
  ) {
    return 'neonGold'
  }

  if (normalized.includes('comfort')) return 'comfort'
  if (normalized.includes('cozy')) return 'cozy'

  // Tübingen / Cologne superior families. Ignore Adlershof "deluxe-superior" category shells.
  if (normalized.includes('superior') && !normalized.includes('deluxe')) {
    return 'superior'
  }

  if (normalized.includes('double')) return 'double'
  if (normalized.includes('penthouse')) return 'penthouse'
  if (normalized.includes('deluxe')) return 'deluxe'
  if (normalized.includes('classic')) return 'classic'
  if (normalized.includes('townhouse') || normalized.includes('tannhaus')) return 'townhouse'

  return null
}

/**
 * Infer room type from a localized apartment title (import/backfill helper).
 * Prefers the leading/base family for compound names (Classic Deluxe → classic,
 * Deluxe Penthouse → deluxe) so modifiers do not split sibling groups.
 */
export function inferRoomTypeFromTitle(title: string): RoomTypeId {
  const normalized = title.trim().toLowerCase()

  if (/\bpine\b/.test(normalized)) return 'pine'
  if (/\bfir\b/.test(normalized)) return 'fir'
  if (/\bcedar\b/.test(normalized)) return 'cedar'
  if (/\bsequoia\b|\bsequioa\b/.test(normalized)) return 'sequoia'

  if (/double\s*deluxe|double-deluxe/.test(normalized)) return 'doubleDeluxe'
  if (/neon\s*silver|silver\s*(?:\/|_|-)?\s*gold|\bsilver\b/.test(normalized)) {
    return 'neonSilverGold'
  }
  if (/neon\s*gold/.test(normalized)) return 'neonGold'

  const leading = normalized
    .replace(/^[^a-zäöüß]+/i, '')
    .split(/[\s/_-]+/)
    .find(Boolean)

  if (leading === 'classic' || leading === 'klassik') return 'classic'
  if (leading === 'deluxe') return 'deluxe'
  if (leading === 'penthouse') return 'penthouse'
  if (leading === 'double') return 'double'
  if (leading === 'comfort') return 'comfort'
  if (leading === 'cozy') return 'cozy'
  if (leading === 'superior') return 'superior'
  // Tübingen Double category units that are not named "Double …"
  if (leading === 'premium' || leading === 'shared') return 'double'
  // Frankfurt Riedberg patio variant lives under the Classic category
  if (leading === 'patio') return 'classic'

  if (/townhouse|tannhaus/.test(normalized)) return 'townhouse'
  if (/\bcomfort\b/.test(normalized)) return 'comfort'
  if (/\bcozy\b/.test(normalized)) return 'cozy'
  if (/\bsuperior\b/.test(normalized)) return 'superior'
  if (/\bdouble\b/.test(normalized)) return 'double'
  if (/\bpenthouse\b/.test(normalized)) return 'penthouse'
  if (/\bdeluxe\b/.test(normalized)) return 'deluxe'
  if (/\bclassic\b|\bklassik\b/.test(normalized)) return 'classic'
  if (/\bgold\b/.test(normalized)) return 'neonGold'

  return 'classic'
}

export type InferRoomTypeFromSourceArgs = {
  title: string
  pathname?: string | null
  categorySlug?: string | null
}

/**
 * Prefer the legacy WordPress category when present; otherwise infer from title.
 */
export function inferRoomTypeFromSource(args: InferRoomTypeFromSourceArgs): RoomTypeId {
  const categorySlug =
    args.categorySlug ?? extractLegacyApartmentCategorySlug(args.pathname ?? null)
  return inferRoomTypeFromCategorySlug(categorySlug) ?? inferRoomTypeFromTitle(args.title)
}
